import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FiSave, FiSend, FiDownload, FiAward, FiTrendingUp, FiUsers, FiCheckCircle } from "react-icons/fi";
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, Tooltip,
} from "recharts";
import {
  getCompetencyLibrary, getMyCompetencyAssessment, saveMyCompetencyAssessment, submitMyCompetencyAssessment,
  getEmployeeCompetencyAssessment, saveEmployeeCompetencyAssessment, submitEmployeeCompetencyAssessment,
  getTeamCompetencyStatus,
} from "../../services/competencyApi";
import { useCycle } from "../../context/CycleContext";
import { useRole } from "../../context/RoleContext";
import useAsync from "../../hooks/useAsync";
import {
  Card, PageHeader, Button, DataTable, Banner, Tabs, StatTile, Loader, ErrorState,
  EmptyState, ProgressBar, ChartFrame,
  BackendPending,
} from "../../components/common";
import { PersonCell } from "../../components/common/Avatar";
import { COMPETENCY_LEVELS, COMPETENCY_STATUS, COMPETENCY_TYPES } from "../../utils/constants";
import { downloadXlsx } from "../../utils/exportExcel";
import { formatScore } from "../../utils/format";
import { getErrorMessage } from "../../utils/errors";
import { VIEWS, FEATURES } from "../../services/navigation";

const api = {
  getCompetencyLibrary, getMyCompetencyAssessment, saveMyCompetencyAssessment, submitMyCompetencyAssessment,
  getEmployeeCompetencyAssessment, saveEmployeeCompetencyAssessment, submitEmployeeCompetencyAssessment,
  getTeamCompetencyStatus,
};

const levelLabel = (value) => COMPETENCY_LEVELS.find((l) => l.value === value)?.label || "Not rated";

function LevelPicker({ value, expected, disabled, onChange, name }) {
  return (
    <div className="level-picker" role="radiogroup" aria-label={name}>
      {COMPETENCY_LEVELS.map((level) => {
        const active = value === level.value;
        const meetsBar = level.value >= expected;
        return (
          <button
            key={level.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            title={`${level.value} — ${level.label}. ${level.hint}`}
            className={`level-dot ${active ? "is-active" : ""} ${meetsBar ? "is-bar" : ""}`}
            onClick={() => onChange(level.value)}
          >
            {level.value}
          </button>
        );
      })}
      <span className="level-text">{value ? levelLabel(value) : "Not rated"}</span>
    </div>
  );
}

function GapChip({ self, manager, expected }) {
  const actual = manager ?? self;
  if (actual === null || actual === undefined) return <span className="badge badge-neutral"><span className="badge-dot" />Not rated</span>;
  const gap = actual - expected;
  if (gap >= 1) return <span className="badge badge-success"><span className="badge-dot" />Above bar +{gap}</span>;
  if (gap === 0) return <span className="badge badge-info"><span className="badge-dot" />At bar</span>;
  return <span className="badge badge-warning"><span className="badge-dot" />Below bar {gap}</span>;
}

function RadarView({ items }) {
  const data = items.map((i) => ({
    competency: i.name.split(" ")[0],
    fullName: i.name,
    Expected: i.expectedLevel,
    Self: i.selfLevel ?? 0,
    Manager: i.managerLevel ?? 0,
  }));
  return (
    <ChartFrame
      label="Competency levels, expected against rated"
      summary={items.map((i) => ({ label: i.name, value: `expected ${i.expectedLevel}, self ${i.selfLevel ?? "—"}, manager ${i.managerLevel ?? "—"}` }))}
    >
      <ResponsiveContainer width="100%" height={300}>
        <RadarChart data={data} outerRadius="62%" margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
          <PolarGrid stroke="#E5E7F0" />
          <PolarAngleAxis dataKey="competency" tick={{ fontSize: 11, fill: "#6B7280" }} />
          <PolarRadiusAxis domain={[0, 5]} tickCount={6} tick={{ fontSize: 10, fill: "#9CA3AF" }} />
          <Radar name="Expected" dataKey="Expected" stroke="#9CA3AF" fill="#9CA3AF" fillOpacity={0.12} />
          <Radar name="Self" dataKey="Self" stroke="#5B4FE0" fill="#5B4FE0" fillOpacity={0.22} />
          <Radar name="Manager" dataKey="Manager" stroke="#12A57A" fill="#12A57A" fillOpacity={0.18} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7F0", fontSize: 13 }} />
        </RadarChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

function AssessmentEditor({ data, mode, onSave, onSubmit, busy }) {
  const [items, setItems] = useState(data.items);
  useEffect(() => setItems(data.items), [data.items]);

  const isManager = mode === "manager";
  const levelKey = isManager ? "managerLevel" : "selfLevel";
  const commentKey = isManager ? "managerComment" : "selfComment";
  const locked = data.locked || (isManager ? false : data.status !== COMPETENCY_STATUS.NOT_STARTED && data.status !== COMPETENCY_STATUS.SELF_DONE);

  const set = (id, key, value) =>
    setItems((list) => list.map((i) => (i.competencyId === id ? { ...i, [key]: value } : i)));

  const rated = items.filter((i) => i[levelKey] !== null && i[levelKey] !== undefined).length;
  const complete = rated === items.length;
  const average = rated ? items.reduce((t, i) => t + (i[levelKey] || 0), 0) / rated : null;
  const gaps = items.filter((i) => (i[levelKey] ?? 99) < i.expectedLevel).length;

  const grouped = COMPETENCY_TYPES.map((type) => ({ type, list: items.filter((i) => i.type === type) })).filter((g) => g.list.length);

  const exportSheet = () =>
    downloadXlsx(`competency-${data.employee.name.replace(/\s+/g, "-").toLowerCase()}`, [
      {
        name: "Competencies",
        title: `Competency assessment — ${data.employee.name}`,
        columns: [
          { key: "name", label: "Competency" },
          { key: "type", label: "Type" },
          { key: "expectedLevel", label: "Expected level" },
          { key: "selfLevel", label: "Self level" },
          { key: "managerLevel", label: "Manager level" },
          { key: "gap", label: "Gap", value: (r) => (r.managerLevel == null ? "" : r.managerLevel - r.expectedLevel) },
          { key: "selfComment", label: "Employee comment" },
          { key: "managerComment", label: "Manager comment" },
        ],
        rows: items,
      },
    ]);

  return (
    <>
      <div className="stat-grid">
        <StatTile label="Competencies" value={items.length} icon={FiAward} hint={`Set for ${data.employee.designationName}`} />
        <StatTile label={isManager ? "You have rated" : "You have rated"} value={`${rated}/${items.length}`} icon={FiCheckCircle} tone={complete ? "success" : "warning"} hint={complete ? "Ready to submit" : "Finish every competency"} />
        <StatTile label="Average level" value={average ? formatScore(average, 1) : "—"} icon={FiTrendingUp} tone="info" hint="Out of 5" />
        <StatTile label="Below the bar" value={gaps} tone={gaps ? "warning" : "success"} hint="Rated under the role expectation" />
      </div>

      <div className="grid-2-1">
        <Card title="Rate each competency" subtitle="1 needs development, 5 role model. The grey ring marks the level expected for this role.">
          {grouped.map((group) => (
            <div key={group.type} className="competency-group">
              <h3 className="section-title">{group.type}</h3>
              {group.list.map((item) => (
                <div className="competency-row" key={item.competencyId}>
                  <div className="competency-head">
                    <div>
                      <p className="competency-name">{item.name}</p>
                      <p className="muted">{item.description}</p>
                    </div>
                    <GapChip self={item.selfLevel} manager={item.managerLevel} expected={item.expectedLevel} />
                  </div>
                  <ul className="competency-behaviours">
                    {item.behaviours.map((b) => <li key={b}>{b}</li>)}
                  </ul>
                  <div className="competency-levels">
                    <span className="competency-expected">Expected: level {item.expectedLevel}</span>
                    <LevelPicker
                      name={item.name}
                      value={item[levelKey]}
                      expected={item.expectedLevel}
                      disabled={locked}
                      onChange={(value) => set(item.competencyId, levelKey, value)}
                    />
                  </div>
                  {isManager && item.selfLevel !== null && (
                    <p className="competency-self-note">
                      Employee rated themselves <strong>{item.selfLevel} — {levelLabel(item.selfLevel)}</strong>
                      {item.selfComment ? `: "${item.selfComment}"` : "."}
                    </p>
                  )}
                  <label className="competency-comment">
                    <span className="form-label">{isManager ? "Your comment" : "Your evidence"}</span>
                    <textarea
                      className="form-control"
                      rows={2}
                      disabled={locked}
                      value={item[commentKey] || ""}
                      placeholder={isManager ? "What you observed this cycle" : "An example that shows this level"}
                      onChange={(e) => set(item.competencyId, commentKey, e.target.value)}
                    />
                  </label>
                </div>
              ))}
            </div>
          ))}
          {!locked && (
            <div className="decision-bar">
              <Button variant="secondary" icon={FiSave} onClick={() => onSave(items)} loading={busy}>Save draft</Button>
              <Button icon={FiSend} disabled={!complete} onClick={() => onSubmit(items)} title={complete ? undefined : "Rate every competency first"}>
                {isManager ? "Submit manager rating" : "Submit self rating"}
              </Button>
            </div>
          )}
        </Card>

        <div className="stack">
          <Card title="Profile" actions={<Button variant="ghost" size="sm" icon={FiDownload} onClick={exportSheet}>Excel</Button>}>
            <RadarView items={items} />
          </Card>
          <Card title="Where this counts">
            <p className="muted">
              Competencies carry <strong>{data.weightage}%</strong> of the final score alongside goals, KRAs and KPIs.
              The manager rating is the one that scores; the self rating drives the conversation.
            </p>
          </Card>
        </div>
      </div>
    </>
  );
}

function CompetencyPageScreen() {
  const { cycleId, cycle } = useCycle();
  const { activeView, profile } = useRole();
  const [params, setParams] = useSearchParams();
  const memberId = params.get("employee");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("framework");

  const isManagerView = activeView === VIEWS.MANAGER;
  const isHrView = activeView === VIEWS.HR || activeView === VIEWS.ADMIN || activeView === VIEWS.MANAGEMENT;

  const mine = useAsync(() => api.getMyCompetencyAssessment(cycleId), [cycleId], { skip: !cycleId || isHrView || isManagerView });
  const team = useAsync(() => api.getTeamCompetencyStatus(cycleId), [cycleId], { skip: !cycleId || (!isManagerView && !isHrView) });
  const member = useAsync(() => api.getEmployeeCompetencyAssessment(cycleId, memberId), [cycleId, memberId], { skip: !cycleId || !memberId });
  const library = useAsync(() => api.getCompetencyLibrary(), [], { skip: !isHrView });

  const teamRows = useMemo(() => team.data || [], [team.data]);
  const summary = useMemo(() => ({
    people: teamRows.length,
    done: teamRows.filter((r) => r.status === COMPETENCY_STATUS.AGREED || r.status === COMPETENCY_STATUS.MANAGER_DONE).length,
    waiting: teamRows.filter((r) => r.status === COMPETENCY_STATUS.SELF_DONE).length,
    gaps: teamRows.reduce((t, r) => t + (r.gapCount || 0), 0),
  }), [teamRows]);

  const saveMine = async (items) => {
    setBusy(true);
    try {
      await api.saveMyCompetencyAssessment(cycleId, { items });
      toast.success("Saved as draft.");
      mine.reload();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };

  const submitMine = async (items) => {
    setBusy(true);
    try {
      await api.saveMyCompetencyAssessment(cycleId, { items });
      await api.submitMyCompetencyAssessment(cycleId);
      toast.success("Self rating sent to your manager.");
      mine.reload();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };

  const saveMember = async (items) => {
    setBusy(true);
    try {
      await api.saveEmployeeCompetencyAssessment(cycleId, memberId, { items });
      toast.success("Saved as draft.");
      member.reload();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };

  const submitMember = async (items) => {
    setBusy(true);
    try {
      await api.saveEmployeeCompetencyAssessment(cycleId, memberId, { items });
      await api.submitEmployeeCompetencyAssessment(cycleId, memberId);
      toast.success("Manager rating submitted.");
      member.reload();
      team.reload();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };

  const exportTeam = () =>
    downloadXlsx(`competency-status-${cycle?.name || ""}`, [
      {
        name: "Competency status",
        title: `Competency status — ${cycle?.name || ""}`,
        columns: [
          { key: "employeeName", label: "Employee" },
          { key: "designationName", label: "Designation" },
          { key: "departmentName", label: "Department" },
          { key: "status", label: "Status" },
          { key: "competencies", label: "Competencies" },
          { key: "selfAverage", label: "Self average", value: (r) => (r.selfAverage ? Number(r.selfAverage.toFixed(2)) : "") },
          { key: "managerAverage", label: "Manager average", value: (r) => (r.managerAverage ? Number(r.managerAverage.toFixed(2)) : "") },
          { key: "gapCount", label: "Below bar" },
        ],
        rows: teamRows,
      },
    ]);

  if (memberId) {
    return (
      <div className="page">
        <PageHeader
          title={member.data ? `${member.data.employee.name}: competencies` : "Competencies"}
          subtitle={member.data ? `${member.data.employee.designationName}, ${cycle?.name}` : cycle?.name}
          backTo="/competencies"
          backLabel="Back to the list"
        />
        {member.error && <ErrorState message={member.error} onRetry={member.reload} />}
        {!member.data && !member.error && <Loader />}
        {member.data && (
          <>
            {member.data.status === COMPETENCY_STATUS.NOT_STARTED && (
              <Banner tone="warning" title="Self rating not submitted">
                You can still record your rating; the employee's view will fill in when they submit theirs.
              </Banner>
            )}
            {member.data.locked && <Banner tone="neutral" title="Agreed and locked">This assessment has been signed off for the cycle.</Banner>}
            <AssessmentEditor data={member.data} mode="manager" onSave={saveMember} onSubmit={submitMember} busy={busy} />
          </>
        )}
      </div>
    );
  }

  if (isManagerView || isHrView) {
    return (
      <div className="page">
        <PageHeader
          title="Competencies"
          subtitle={`Behaviour ratings for ${cycle?.name}. Levels run 1 to 5 against the bar set for each role.`}
          actions={<Button variant="secondary" icon={FiDownload} onClick={exportTeam} disabled={!teamRows.length}>Export Excel</Button>}
        />

        <div className="stat-grid">
          <StatTile label="People" value={summary.people} icon={FiUsers} hint={isHrView ? "In scope" : "Your direct reports"} />
          <StatTile label="Completed" value={summary.done} icon={FiCheckCircle} tone="success" hint="Manager rating recorded" />
          <StatTile label="Waiting on you" value={summary.waiting} tone="warning" hint="Self rating submitted, yours pending" />
          <StatTile label="Below the bar" value={summary.gaps} tone={summary.gaps ? "danger" : "success"} hint="Competency gaps to act on" />
        </div>

        {isHrView && (
          <Tabs
            value={tab}
            onChange={setTab}
            tabs={[{ key: "framework", label: "People", count: teamRows.length }, { key: "library", label: "Framework", count: (library.data || []).length }]}
          />
        )}

        {(!isHrView || tab === "framework") && (
          <Card padded={false} title="Assessment status">
            {team.loading && !team.data ? <Loader /> : (
              <DataTable
                rows={teamRows}
                rowKey="employeeId"
                onRowClick={(row) => setParams({ employee: row.employeeId })}
                emptyTitle="Nobody to rate"
                emptyText="Competency ratings appear here once the cycle opens."
                columns={[
                  { key: "employeeName", label: "Employee", render: (r) => <PersonCell name={r.employeeName} meta={`${r.designationName}, ${r.departmentName}`} /> },
                  {
                    key: "status",
                    label: "Status",
                    render: (r) => {
                      const tone = { [COMPETENCY_STATUS.AGREED]: "success", [COMPETENCY_STATUS.MANAGER_DONE]: "info", [COMPETENCY_STATUS.SELF_DONE]: "warning", [COMPETENCY_STATUS.NOT_STARTED]: "neutral" }[r.status];
                      return <span className={`badge badge-${tone}`}><span className="badge-dot" />{r.status}</span>;
                    },
                  },
                  { key: "selfAverage", label: "Self avg", align: "right", render: (r) => (r.selfAverage ? formatScore(r.selfAverage, 1) : "—") },
                  { key: "managerAverage", label: "Manager avg", align: "right", render: (r) => (r.managerAverage ? formatScore(r.managerAverage, 1) : "—") },
                  {
                    key: "gapCount",
                    label: "Gaps",
                    align: "right",
                    render: (r) => (r.gapCount ? <span className="badge badge-warning"><span className="badge-dot" />{r.gapCount}</span> : <span className="muted">None</span>),
                  },
                  { key: "act", label: "", align: "right", render: (r) => <Button variant="secondary" size="sm">Rate</Button> },
                ]}
              />
            )}
          </Card>
        )}

        {isHrView && tab === "library" && (
          <Card padded={false} title="Competency framework" subtitle="The behaviours every role is rated against.">
            {library.loading && !library.data ? <Loader /> : (
              <DataTable
                rows={library.data || []}
                pageSize={10}
                columns={[
                  { key: "name", label: "Competency", render: (r) => <span className="cell-stack"><strong>{r.name}</strong><small>{r.description}</small></span> },
                  { key: "type", label: "Type", render: (r) => <span className="tag tag-info">{r.type}</span> },
                  { key: "behaviours", label: "Observable behaviours", render: (r) => <ul className="icon-list">{r.behaviours.map((b) => <li key={b}>{b}</li>)}</ul> },
                  { key: "active", label: "Status", render: (r) => <span className={`badge badge-${r.active ? "success" : "neutral"}`}><span className="badge-dot" />{r.active ? "Active" : "Retired"}</span> },
                ]}
              />
            )}
          </Card>
        )}
      </div>
    );
  }

  if (mine.error) return <ErrorState message={mine.error} onRetry={mine.reload} />;
  if (!mine.data) return <Loader />;

  return (
    <div className="page">
      <PageHeader
        title="My competencies"
        subtitle={`${cycle?.name}. Rate yourself against the bar for ${mine.data.employee.designationName}.`}
      />
      {mine.data.status === COMPETENCY_STATUS.SELF_DONE && (
        <Banner tone="info" title="Self rating submitted">
          Your rating is with {profile?.manager?.name || "your manager"}. You'll see their view once they submit theirs.
        </Banner>
      )}
      {mine.data.status === COMPETENCY_STATUS.MANAGER_DONE && (
        <Banner tone="success" title="Your manager has rated you">Open each competency to compare the two views before your review meeting.</Banner>
      )}
      {mine.data.status === COMPETENCY_STATUS.AGREED && (
        <Banner tone="neutral" title="Agreed">Both ratings are locked for this cycle.</Banner>
      )}
      {mine.data.items.length === 0 ? (
        <EmptyState title="No competencies mapped" text="HR has not mapped a competency set to your designation yet." />
      ) : (
        <>
          <Card title="Completion">
            <ProgressBar
              value={(mine.data.items.filter((i) => i.selfLevel !== null).length / mine.data.items.length) * 100}
              showValue
              label="Self rating completion"
            />
          </Card>
          <AssessmentEditor data={mine.data} mode="self" onSave={saveMine} onSubmit={submitMine} busy={busy} />
        </>
      )}
    </div>
  );
}

const NEEDED_ENDPOINTS = [
  ["GET", "/competencies", "Competency library (framework)"],
  ["POST / PUT", "/competencies, /competencies/{id}", "HR maintains the library"],
  ["GET / PUT", "/cycles/{cycleId}/competencies/me", "Employee self rating (draft)"],
  ["POST", "/cycles/{cycleId}/competencies/me/submit", "Employee submits self rating"],
  ["GET / PUT", "/cycles/{cycleId}/employees/{employeeId}/competencies", "Manager rating of a report"],
  ["POST", "/cycles/{cycleId}/employees/{employeeId}/competencies/submit", "Manager submits rating"],
  ["GET", "/cycles/{cycleId}/competencies/team", "Team / organisation status"],
];

export default function CompetencyPage() {
  if (!FEATURES.competency) {
    return <BackendPending title="Competencies" purpose="Competency framework, self and manager ratings." endpoints={NEEDED_ENDPOINTS} flag="REACT_APP_FEATURE_COMPETENCY" />;
  }
  return <CompetencyPageScreen />;
}
